import React, { useState, FormEvent, useEffect, useMemo } from 'react'
import { connect, useDispatch } from 'react-redux'
import { Stack, Paper, TextField, IconButton, Tooltip, CircularProgress } from '@mui/material'
import ProgressButton from '../../../Utils/ProgressButton'
import { entityTextField } from '../../../Utils/EntityField'
import { AppState } from '../../../../reducers/index'
import { upsertComplaintInternalMessage } from '../../../../actions/complaint/complaintInternalMessageUpsert'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { fetchComplaintInternalMessage } from '../../../../actions/complaint/complaintInternalMessageDetail'
import moment from 'moment'
import ComplaintInternalMessage from '../../../../models/ComplaintInternalMessage'
import { ComplaintInternalMessageDetailState } from '../../../../reducers/complaint/complaintInternalMessageDetail'
import { fetchComplaintInternalMessages } from '../../../../actions/complaint/complaintInternalMessage'
import { uploadComplaintInternalMessageFile } from '../../../../actions/complaint/complaintInternalMessageUploadFile'
import { AttachFile } from '@mui/icons-material';
import { useThemeContext } from '../../../../theme/ThemeContext'

interface Props {
  message: ComplaintInternalMessageDetailState
}

const empty: ComplaintInternalMessage = {
  date: moment().tz("America/Argentina/Buenos_Aires").format(),
  message: '',
  status: 'U',
  complaintId: 0,
  ownerName: '',
}

const ComplaintInternalMessageUpsert: React.FC<Props> = ({ message }) => {
  const [changed, setChanged] = useState<boolean>(false)
  const [fetchingUploadFile, setFetchingUploadFile] = useState<boolean>(false)
  const [complaintInternalMessageInfo, setComplaintInternalMessageInfo] = useState<ComplaintInternalMessage>(message.message || empty)
  const [complaintInternalMessageId, setComplaintInternalMessageId] = useState<number | undefined>(undefined)
  const [complaintInternalMessageFileId, setComplaintInternalMessageFileId] = useState<string | undefined>(undefined)
  const [readMessages, setReadMessages] = useState<boolean | undefined>(undefined)
  const [attached, setAttached] = useState<boolean | undefined>(undefined)
  const [showUploadFile, setShowUploadFile] = useState<boolean>(false)
  const fileInput = React.useRef<HTMLInputElement>(null);
  const params = useParams<{ complaintId: string, complaintInternalMessageId: string }>()
  const dispatch = useDispatch()
  
  const { t } = useTranslation('complaint')
  const { theme } = useThemeContext()

  useEffect(() => {
    if (params.complaintInternalMessageId) {
      dispatch(fetchComplaintInternalMessage(params.complaintInternalMessageId))
    }
  }, [dispatch, params.complaintInternalMessageId])


  const change = (newComplaintInternalMessageInfo: ComplaintInternalMessage) => {
    setComplaintInternalMessageInfo(newComplaintInternalMessageInfo)
    setChanged(newComplaintInternalMessageInfo.message.trim() !== '')
  }

  const handleOnSubmit = (event: FormEvent) => {
    event.preventDefault()
    changeComplaint()
  }

  const handleOnKeydown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && changed) {
      event.preventDefault()
      event.stopPropagation()
      changeComplaint()
    }
  }

  const setAttachedFileInfo = async () => {
    const files = (fileInput as any).current.files as File[];
    if (files.length && files[0]) {
      setComplaintInternalMessageInfo({
        ...complaintInternalMessageInfo,
        message: complaintInternalMessageInfo.message + (complaintInternalMessageInfo.message ? ' / ' : '') + `Se envía correo electrónico con el archivo adjunto ${files[0].name}`
      })
      setChanged(true)
      setAttached(true)
    }
  }

  const handleAttachedFile = async () => {
    const files = (fileInput as any).current.files as File[];
    if (!files.length) 
      {
      setReadMessages(true)
      return;
      }
    if (files[0]) {
      const formData = new FormData();
      formData.append("file", files[0]);
      formData.append("fileName", files[0].name);
      dispatch(uploadComplaintInternalMessageFile(formData, complaintInternalMessageInfo.complaintId,
        (fileId: string) => {
          setComplaintInternalMessageFileId(fileId);
          setComplaintInternalMessageInfo({ ...complaintInternalMessageInfo, fileId: fileId })
        }))
    }
  }


  const handleComplaintInternalMessageId = (messageId: number) => setComplaintInternalMessageId(messageId)

  const changeComplaint = async () => {
    setChanged(false)
    setFetchingUploadFile(true)
    await dispatch(upsertComplaintInternalMessage({ ...complaintInternalMessageInfo, date: moment().tz("America/Argentina/Buenos_Aires").format()}, handleComplaintInternalMessageId))
  }

  useEffect(() => {
    if (complaintInternalMessageId) handleAttachedFile()
// eslint-disable-next-line react-hooks/exhaustive-deps      
  }, [complaintInternalMessageId])

  useEffect(() => {
    if (complaintInternalMessageFileId) {
      dispatch(upsertComplaintInternalMessage({ ...complaintInternalMessageInfo, date: moment().tz("America/Argentina/Buenos_Aires").format(), fileId: complaintInternalMessageFileId, id: complaintInternalMessageId }, 
          () => setReadMessages(true)))
    }
// eslint-disable-next-line react-hooks/exhaustive-deps          
  }, [complaintInternalMessageFileId, complaintInternalMessageId, dispatch])

  useEffect(() => {
    if (readMessages) {
      setFetchingUploadFile(false)
      dispatch(fetchComplaintInternalMessages(params.complaintId))
      setReadMessages(false)
    }
  }, [readMessages, params.complaintId, dispatch])



  useEffect(() => {
    if (message.message)
      setComplaintInternalMessageInfo(params.complaintInternalMessageId ? message.message : empty)
  }, [message.message, params.complaintInternalMessageId, params.complaintId])


  useEffect(() => {
    if (params.complaintId)
      setComplaintInternalMessageInfo({ ...complaintInternalMessageInfo, complaintId: Number(params.complaintId) })
  }, [complaintInternalMessageInfo, params.complaintId])

  const ComplaintInternalMessageInfoField = useMemo(() => entityTextField(change), [])

  return (
    <Paper style={{ borderRadius: "10px", padding: "10px" }}>
      <form onSubmit={handleOnSubmit} onKeyDown={handleOnKeydown}>
        <Stack display="flex" flexDirection='row' justifyContent="space-between">
          <ComplaintInternalMessageInfoField
            key="message"
            variant="outlined"
            label={t("message")}
            entity={complaintInternalMessageInfo}
            attr="message"
            size="small"
            required
          />
          <Tooltip title={t('attachFile')}>
            <IconButton disabled={attached} size="small" sx={{ padding: "5px" }} onClick={(event: FormEvent) => { event.preventDefault(); event.stopPropagation(); setShowUploadFile(!showUploadFile) }}>
              <AttachFile sx={{ color: theme.typography.caption.color }} />
            </IconButton>
          </Tooltip>
          <TextField
            InputLabelProps={{ shrink: true }}
            inputProps={{ accept: "application/pdf" }}
            inputRef={fileInput}
            label={t('attachFile')}
            margin="normal"
            size='small'
            variant="outlined"
            onChange={() => setAttachedFileInfo()}
            type="file"
            sx={{ marginLeft: "15px", display: showUploadFile ? 'default' : 'none' }}
            fullWidth
            disabled={attached}
          />
          <ProgressButton state={message} disabled={!changed} style={{ margin: "8px 0px 0px 8px" }}>
            {t('send')}
          </ProgressButton>
          {fetchingUploadFile &&
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: "4px" }}>
              <CircularProgress size={25} />
            </div>
          }
        </Stack>
      </form>
    </Paper>

  )
}

const mapStateToProps = (state: AppState) => ({
  message: state.complaintInternalMessageDetail
})

export default connect<Props, {}, {}, AppState>(mapStateToProps, {})(ComplaintInternalMessageUpsert)
